// TODO: document me
export interface IAppEnvironment {
  backendUrl: string;
  graphqlUrl: string;
  analyticsUrl: string;
  /** Base URL for Clips Studio (e.g. https://clips.feed.fm). Used for invite/signup links. */
  clipsStudioUrl: string;
  cookieDomain: string;
  dashboardBaseUrl: string;
  dashboardCookieName: string;
  feedBaseEnvironment: string;
  appMode: 'production' | 'development';
  baseDeveloperUrl: string;
  baseBusinessUrl: string;
  baseRadioUrl: string;
  storybookDevelopmentUrl: string;
}

/**
 * The "login as" feature relies on setting cookies one subdomain up from the request. In other words, the app at
 * "ops.stage.adaptr.com" sets cookies using the domain "stage.adaptr.com". This feature can't work in non-standard
 * URLs (ie dev environments), so the current hostname is used as a harmless no-op.
 */
const getCookieDomain = ( hostname: string ) => hostname.replace( /ops\.(.*)$/, '$1' );

const getDashboardCookieName = ( dashboardBaseUrl: string ) =>
  dashboardBaseUrl === 'https://stage.adaptr.com/dashboard' ? 'staging-token' : 'production-token';

function getEnvironment( host: string ): IAppEnvironment {
  const appMode = process.env.appMode === 'development' ? 'development' : 'production';
  const backendUrl = process.env.FMG_BACKEND_URL || 'https://stage.feed.fm';
  const graphqlUrl = process.env.FMG_GRAPHQL_URL || `${backendUrl}/graphql-services`;
  const analyticsUrl = process.env.FMG_ANALYTICS_URL || `${backendUrl}/analytics`;
  const storybookDevelopmentUrl =
    appMode === 'development'
      ? process.env.FMG_STORYBOOK_DEVELOPMENT_URL || 'http://localhost:6006'
      : '/library/index.html';

  if ( !process.env.FMG_BACKEND_URL ) {
    console.warn(
      `No FMG_BACKEND_URL configured. Defaulting to stage.feed.fm. Consider configuring <a href="https://www.npmjs.com/package/@feedfm/feed-cli">Feed CLI</a> for local development. You should never see this in stage or production.`,
    );
  }

  const [hostname] = host.split( ':' );
  // note: webpack does not expand 'process.env' to be an object with all env variables
  // (for security reasons) - you need to use process.env.feedBaseEnvironment directly
  const feedBaseEnvironment = process.env.feedBaseEnvironment;
  const localOrStage = feedBaseEnvironment === 'local' || feedBaseEnvironment === 'stage';
  const baseDeveloperUrl = `//developer.${localOrStage ? `${feedBaseEnvironment}.` : ''}feed.fm`;
  const baseBusinessUrl = `//business.${localOrStage ? `${feedBaseEnvironment}.` : ''}feed.fm`;
  const baseRadioUrl = `//radio.${localOrStage ? `${feedBaseEnvironment}.` : ''}feed.fm`;

  const clipsStudioUrl =
    process.env.FMG_CLIPS_STUDIO_URL ||
    ( feedBaseEnvironment === 'local' || feedBaseEnvironment === 'stage'
      ? `https://clips.${feedBaseEnvironment}.feed.fm`
      : 'https://clips.feed.fm' );

  return {
    appMode,
    backendUrl,
    graphqlUrl,
    analyticsUrl,
    clipsStudioUrl,
    dashboardBaseUrl: process.env.dashboardBaseUrl || '',
    cookieDomain: getCookieDomain( hostname || '' ),
    dashboardCookieName: getDashboardCookieName( process.env.dashboardBaseUrl || '' ),
    feedBaseEnvironment: feedBaseEnvironment || '',
    baseDeveloperUrl,
    baseBusinessUrl,
    baseRadioUrl,
    storybookDevelopmentUrl,
  };
}

export const environment = typeof window !== 'undefined' ? getEnvironment( window.location?.host ) : getEnvironment( '' );

if ( typeof window !== 'undefined' ) {
  ( window as any ).environment = environment;
}
